import { change, clearFields } from 'redux-form';
import { compose, lifecycle } from 'recompose';
import { connect } from 'react-redux';
import moment from 'moment';
import React, { useEffect } from 'react';

import {
  fetchDecisionSplitPathObjectFields,
  setPathObjectField,
} from '../../../../actions/JourneyBuilder';
import { fieldDataTypes, JOURNEY_BUILDER_FORM_NAME } from '../../../../constants/JourneyBuilder';
import DateObjectField from './DateObjectField';
import DependentListObjectField, { getDependentOptions } from './DependentListObjectField';
import DictObjectField from './DictObjectField';
import DynamicListObjectField from './DynamicListObjectField';
import FloatObjectField from './FloatObjectField';
import IntegerObjectField from './IntegerObjectField';
import ListObjectField from './ListObjectField';
import TextObjectField from './TextObjectField';

export const getFormFieldName = (branchResult, fieldCode) => `${branchResult}_field_${fieldCode}`;

export const getDefaultFieldValue = (field, topicName, fieldValues) => {
  const options = field && field.criteria && field.criteria.options;
  const topicNameUpperCase = topicName && topicName.toUpperCase();
  const dataType = field && field.criteria && field.criteria.dataType;
  const dependentOptions =
    dataType === fieldDataTypes.DEPENDENTLIST && getDependentOptions(field, fieldValues);

  switch (dataType) {
    case fieldDataTypes.INTEGER:
      return 0;
    case fieldDataTypes.FLOAT:
      return 0.0;
    case fieldDataTypes.DATE:
      return moment().toDate();
    case fieldDataTypes.DICT:
      return field.criteria.values[Object.getOwnPropertyNames(field.criteria.values)[0]];
    case fieldDataTypes.LIST:
      return (
        (options &&
          options.length &&
          ((field.criteria.defaultValue &&
            options.find(x => x.value === field.criteria.defaultValue) &&
            options.find(x => x.value === field.criteria.defaultValue).value) ||
            (options.find(x => x.value.toUpperCase() === topicNameUpperCase) &&
              options.find(x => x.value.toUpperCase() === topicNameUpperCase).value) ||
            options[0].value)) ||
        null
      );
    case fieldDataTypes.DYNAMICLIST:
      return (
        (options &&
          options.length &&
          ((field.criteria.defaultValue &&
            options.find(x => x.value === field.criteria.defaultValue) &&
            options.find(x => x.value === field.criteria.defaultValue).value) ||
            (options.find(x => x.value.toUpperCase() === topicNameUpperCase) &&
              options.find(x => x.value.toUpperCase() === topicNameUpperCase).value) ||
            options[0].value)) ||
        null
      );
    case fieldDataTypes.DEPENDENTLIST:
      return (dependentOptions && dependentOptions.length && dependentOptions[0].value) || null;

    case fieldDataTypes.TEXT:
    default:
      return '';
  }
};

export const getFieldLabel = (fields, code, fieldValues) => {
  const field = fields && fields.find(x => x.code === code);
  const dataType = field && field.criteria && field.criteria.dataType;
  const value = fieldValues[code];
  const dependentOptions =
    dataType === fieldDataTypes.DEPENDENTLIST && getDependentOptions(field, fieldValues);

  if (!field || !field.criteria || !field.criteria.dataType) {
    return '';
  }

  switch (dataType) {
    case fieldDataTypes.LIST:
      return (
        (field.criteria.options || []).find(x => x.value === value) || {
          label: '',
        }
      ).label;

    case fieldDataTypes.DYNAMICLIST:
      return (
        (field.criteria.options || []).find(x => x.value === value) || {
          label: '',
        }
      ).label;

    case fieldDataTypes.INTEGER:
    case fieldDataTypes.FLOAT:
    case fieldDataTypes.DATE:
    case fieldDataTypes.DICT:
    case fieldDataTypes.TEXT:
      return value;
    case fieldDataTypes.DEPENDENTLIST:
      return (
        (dependentOptions || []).find(x => x.value === value) || {
          label: '',
        }
      ).label;

    default:
      return '';
  }
};

export const PathObjectField = ({
  setPathObjectFieldDispatch,
  field,
  fieldValue,
  index,
  readonlyForm,
  item: {
    arguments: { branchResult },
    metaData,
  },
  dependentsOnField,
  setFormValueDispatch,
  culture,
  currentMID,
  objId,
  fetchPathObjectFieldsDispatch,
}) => {
  const dataType = field && field.criteria && field.criteria.dataType;
  const handleChange = value => {
    setPathObjectFieldDispatch(index, field.code, value);
  };

  const onPathObjectFieldChange = value => {
    handleChange(value);
    if (value === 'RSVP STATUS') {
      fetchPathObjectFieldsDispatch(objId, value, field, culture, currentMID);
    }
  };

  switch (dataType) {
    case fieldDataTypes.INTEGER:
      return (
        <IntegerObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          onChange={handleChange}
        />
      );

    case fieldDataTypes.FLOAT:
      return (
        <FloatObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          onChange={handleChange}
        />
      );

    case fieldDataTypes.DATE:
      return (
        <DateObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          onChange={handleChange}
        />
      );

    case fieldDataTypes.TEXT:
      return (
        <TextObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          onChange={handleChange}
        />
      );

    case fieldDataTypes.DICT:
      return (
        <DictObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          values={field.criteria.values}
          onChange={handleChange}
        />
      );

    case fieldDataTypes.LIST:
      return (
        <ListObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          options={field.criteria.options}
          onChange={handleChange}
        />
      );
    case fieldDataTypes.DEPENDENTLIST:
      return (
        <DependentListObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          metaData={metaData}
          criteria={field.criteria}
          onChange={handleChange}
          code={field.code}
          dependentsOnField={{
            ...dependentsOnField,
            formFieldName: getFormFieldName(branchResult, dependentsOnField.code),
          }}
          setFormValueDispatch={setFormValueDispatch}
          setPathObjectFieldDispatch={setPathObjectFieldDispatch}
        />
      );

    case fieldDataTypes.DYNAMICLIST:
      useEffect(() => {
        onPathObjectFieldChange(fieldValue);
      }, []);
      return (
        <DynamicListObjectField
          name={getFormFieldName(branchResult, field.code)}
          label={field.name}
          readonlyForm={readonlyForm}
          options={field.criteria.options}
          onChange={onPathObjectFieldChange}
        />
      );

    default:
      return <React.Fragment />;
  }
};

PathObjectField.displayName = 'PathObjectField';

export const mapStateToProps = (state, ownProps) => {
  const {
    journeyBuilderArea: {
      JourneyBuilderSettings: {
        topicName,
        localeInfo: { locale },
      },
    },
  } = state;
  const {
    field,
    fields,
    item: {
      metaData: { fieldValues },
    },
  } = ownProps;
  let dependentsOnField = null;
  dependentsOnField =
    field &&
    fields &&
    field.criteria &&
    field.criteria.dataType === fieldDataTypes.DEPENDENTLIST &&
    fields.find(x => x.code === field.criteria.dependentsOnFieldName);
  return {
    locale,
    topicName,
    dependentsOnField,
    fieldValue:
      (fieldValues && fieldValues[field.code]) ||
      getDefaultFieldValue(field, topicName, fieldValues),
  };
};

export const mapDispatchToProps = dispatch => ({
  clearFormValueDispatch(fieldName) {
    dispatch(clearFields(JOURNEY_BUILDER_FORM_NAME, false, false, fieldName));
  },
  setFormValueDispatch(fieldName, value) {
    dispatch(change(JOURNEY_BUILDER_FORM_NAME, fieldName, value));
  },
  setPathObjectFieldDispatch(index, fieldName, value) {
    dispatch(setPathObjectField(index, fieldName, value));
  },
  fetchPathObjectFieldsDispatch(objId, value, field, culture, currentMID) {
    dispatch(fetchDecisionSplitPathObjectFields(objId, value, field, culture, currentMID));
  },
});

export const PathObjectFieldLoader = lifecycle({
  componentDidMount() {
    const {
      setFormValueDispatch,
      setPathObjectFieldDispatch,
      field,
      index,
      topicName,
      item: {
        arguments: { branchResult },
        metaData: { fieldValues },
      },
    } = this.props;

    const formFieldName = getFormFieldName(branchResult, field.code);
    const fieldValue =
      (fieldValues && fieldValues[field.code]) ||
      getDefaultFieldValue(field, topicName, fieldValues);

    setFormValueDispatch(formFieldName, fieldValue);
    setPathObjectFieldDispatch(index, field.code, fieldValue);
  },
  componentWillUnmount() {
    const {
      clearFormValueDispatch,
      field,
      item: {
        arguments: { branchResult },
      },
    } = this.props;

    const formFieldName = getFormFieldName(branchResult, field.code);
    clearFormValueDispatch(formFieldName);
  },
})(PathObjectField);

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps
  )
)(PathObjectFieldLoader);



// WEBPACK FOOTER //
// ./src/JourneyBuilder/DecisionSplit/components/Path/PathObjectFields/index.js